import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuContext } from '../../../components/ui/menuContext';
import { Loader2, Pencil, Trash2 } from 'lucide-react';
import { buildInvestorProfilePath } from '../../../components/investor-display/helpers';
import { useCRM } from '../../../hooks/crm';

const DraggableItemMenu = ({
  closeMenu,
  boardId,
  columnId,
  investorId,
  investorIdField,
  investorName,
  onEditNote,
  note,
  onEditValue,
  value,
  menuTrigger,
}) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [actionError, setActionError] = useState('');
  const {
    removeInvestorFromBoard,
    updateInvestorNote,
    updateInvestorValue,
  } = useCRM();

  const generateLink = useCallback(() => {
    return buildInvestorProfilePath({
      investor: { id: investorIdField, name: investorName },
      language: i18n.language,
      inApp: true,
    });
  }, [i18n.language, investorIdField, investorName]);

  const handleRemoveInvestor = async () => {
    if (isRemoving) return;

    setIsRemoving(true);
    setActionError('');
    try {
      await removeInvestorFromBoard({
        boardId,
        columnId,
        investorId,
      });
      setIsOpen(false);
      closeMenu?.();
    } catch (error) {
      console.error(error);
      setActionError(t('app:crm.t70'));
    } finally {
      setIsRemoving(false);
    }
  };

  const handleRemoveNote = async () => {
    try {
      await updateInvestorNote({
        boardId,
        columnId,
        investorId,
        note: '',
      });
      closeMenu?.();
    } catch (error) {
      console.error(error);
    }
  };

  const handleRemoveValue = async () => {
    try {
      await updateInvestorValue({
        boardId,
        columnId,
        investorId,
        value: null,
      });
      closeMenu?.();
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenProfile = () => {
    closeMenu?.();
    window.open(generateLink(), '_blank', 'noopener,noreferrer');
  };

  const valueIsSet = parseFloat(value) > 0;

  const menuItems = [
    {
      label: isRemoving ? t('app:crm.t69') : t('app:crm.t16'),
      onClick: handleRemoveInvestor,
      icon: isRemoving ? Loader2 : Trash2,
      disabled: isRemoving,
      keepOpen: true,
      variant: 'destructive',
    },
    ...(actionError ? [{ label: actionError, disabled: true }] : []),
    {
      label: t('app:crm.t48'),
      onClick: handleOpenProfile,
    },
    { separator: true },
    {
      label: note ? t('app:crm.t17') : t('app:crm.t18'),
      onClick: () => {
        onEditNote();
        closeMenu?.();
      },
      icon: Pencil,
    },
    ...(
      note
        ? [
          {
            label: t('app:crm.t19'),
            onClick: handleRemoveNote,
            icon: Trash2,
          },
        ]
        : []
    ),
    { separator: true },
    {
      label: valueIsSet ? t('app:crm.t20') : t('app:crm.t21'),
      onClick: () => {
        onEditValue();
        closeMenu?.();
      },
      icon: Pencil,
    },
    ...(
      valueIsSet
        ? [
          {
            label: t('app:crm.t22'),
            onClick: handleRemoveValue,
            icon: Trash2,
          },
        ]
        : []
    ),
  ];

  return (
    <MenuContext
      open={isOpen}
      onOpenChange={(nextOpen) => {
        setIsOpen(nextOpen);
        if (!nextOpen) setActionError('');
      }}
      trigger={menuTrigger}
      items={menuItems}
    />
  );
};

export default DraggableItemMenu;
